/* ====== Update Dynamic Lead Time on Cart Page ====== */

/* ====== Fetches fresh lead-time data for every eligible cart line-item, compares it
   against the value already stored in the "Lead Time" line-item property,
   and – if it has changed – patches the UI and persists the new value via /cart/change.js. ====== */

(function () {
  'use strict';

  /* ====== Config ====== */
  const API_BASE = cartApiUrl;
  /* ====== Property key must exactly match the Liquid template & Shopify property name ====== */
  const LEAD_TIME_KEY = 'Lead Time';

  /* ====== Product IDs that must never be patched ====== */
  const SKIP_PRODUCT_IDS = new Set([7374146306136, 8267633524824]);

  /* ====== Helper Functions ====== */

  /* ====== Returns true when the line-item is a ghost / add-on that should be excluded from lead-time processing (gift-wrap, companion stones, etc.). ====== */
  function shouldSkipItem(item) {
    const p = item.properties || {};
    const idkmsFlag = p._is_idkms_included;
    const isExplicitlyExcluded = idkmsFlag === 'false' || idkmsFlag === false;

    /* ====== Engraved line items carry their own lead time (base + engraving note), so never overwrite it ====== */
    const isEngravingRequired = String(p._engraving_required || '').trim().toLowerCase() === 'yes';

    return (
      p._sccl_associated_variant_id          !== undefined ||
      p._complimentary_associated_variant_id !== undefined ||
      p._stone_associated_variant_id         !== undefined ||
      p._associated_variant_id               !== undefined ||
      p.DiamondId                            !== undefined ||
      p._associated_link_variant_id          !== undefined ||
      SKIP_PRODUCT_IDS.has(item.product_id)                ||
      isExplicitlyExcluded                                 ||
      isEngravingRequired                                  ||
      item.product_type === 'Bundle Sets'
    );
  }

  /* ======
   * Derives a lead-time data from the API response.
   * Rules: 
   *   availableToSell <= 0  OR  no inventory_management  → use existing custom lead time from DOM
   *   availableToSell >  30  → 'In Stock'
   *   availableToSell  1-30  → 'Only a few left!'
   * Returns '' when no value can be determined (prevents an unnecessary PATCH). ====== */
  function deriveLeadTime(apiData, existingLeadTime) {
    if (!apiData) return '';

    const qty = apiData.availableToSell;
    if (qty <= 0 || !apiData.inventory_management) {
      /* ====== Prefer the API's custom lead time; fall back to the existing property value Done ====== */
      return apiData.customLeadTime || existingLeadTime || '';
    }
    if (qty > 30) return 'In Stock';
    if (qty > 0)  return 'Only a few left!';

    return '';
  }

  /* ====== API layer ====== */

  /* ====== Simple in-memory request de-duplicator so we never fire the same call twice. ====== */
  const _leadTimeCache = new Map();

  /* ====== Fetches lead-time data for one line-item + unit quantity :: Returns the parsed JSON body (or null on failure). ====== */
  async function fetchLeadTime(item, unit) {
    const key = `${item.product_id}_${item.variant_id}_${unit}`;

    if (_leadTimeCache.has(key)) return _leadTimeCache.get(key);

    const promise = fetch(
      `${API_BASE}/mongo/leadtimes?shopifyProductId=${item.product_id}&variantId=${item.variant_id}&unit=${unit}`,
      { headers: { 'Content-Type': 'application/json' } }
    )
    .then(r => (r.ok ? r.json() : null))
    .catch(() => null);

    _leadTimeCache.set(key, promise);
    return promise;
  }

  /* ====== Cart Data fetch: Returns the fresh Shopify cart object ====== */
  async function fetchCart() {
    const r = await fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' },
    });
    return r.json();
  }

  /* ====== UI update ====== */

  /* ====== Finds the cart-item <tr> by item.key (stored on data-item-key of the remove button
    and the quantity input — both reliable anchors present even when qty selector is hidden).
   * Falls back to matching by variant_id if no key match found (safety net).
   * The Liquid template renders lead time as: <p class="cart-item-property rc-cart-item-property--lead-time"><span class="cart-item-property__value">…</span></p>
    ====== */
  function findCartRow(item) {
    /* ====== Primary: match on item.key via the quantity input's data-item-key attribute ====== */
    const byKey = document.querySelector(
      `tr.cart-item input.quantity-input[data-item-key="${item.key}"]`
    );
    if (byKey) return byKey.closest('tr.cart-item');

    /* ====== Secondary: match on item.key via the remove button's data-item-key attribute ====== */
    const byRemove = document.querySelector(
      `tr.cart-item cart-remove[data-item-key="${item.key}"]`
    );
    if (byRemove) return byRemove.closest('tr.cart-item');

    /* ====== Fallback: first row matching the variant id (handles hidden qty selector case) ====== */
    const byVariant = document.querySelector(
      `tr.cart-item [data-quantity-variant-id="${item.variant_id}"]`
    );
    if (byVariant) return byVariant.closest('tr.cart-item');

    return null;
  }

  function updateLeadTimeInDOM(item, newLeadTime) {
    const cartItemRow = findCartRow(item);
    if (!cartItemRow) {
      console.warn('[RC Lead Time] Could not find cart row for item key:', item.key);
      return;
    }

    const propertiesWrapper = cartItemRow.querySelector(
      '.cart-item-details .cart-item-properties'
    );
    if (!propertiesWrapper) {
      console.warn('[RC Lead Time] Could not find .cart-item-properties for item key:', item.key);
      return;
    }

    let leadTimePara = propertiesWrapper.querySelector('.rc-cart-item-property--lead-time');

    if (newLeadTime && newLeadTime.trim()) {
      /* ====== Update or insert ====== */
      if (!leadTimePara) {
        leadTimePara = document.createElement('p');
        leadTimePara.className = 'cart-item-property rc-cart-item-property--lead-time';

        /* ====== Value-only display (no key label) — matches value_only_properties in Liquid ====== */
        const valueSpan = document.createElement('span');
        valueSpan.className = 'cart-item-property__value';
        leadTimePara.appendChild(valueSpan);

        propertiesWrapper.appendChild(leadTimePara);
      }

      leadTimePara.querySelector('.cart-item-property__value').textContent = newLeadTime.trim();

    } else if (leadTimePara) {
      /* ====== Remove stale element ====== */
      leadTimePara.remove();
    }
  }

  /* ====== Line-item property persistence ====== */

  /* ====== Sends /cart/change.js for a single item with its updated properties object.
     Processes items one-at-a-time (sequential) to avoid Shopify race conditions. ====== */
  async function persistLeadTime(item) {
    /* ====== Merge updated lead time; use computed property name so the space in the key is preserved ====== */
    const properties = Object.assign({}, item.properties, {
      [LEAD_TIME_KEY]: item._newLeadTime,
    });

    /* ====== Preserve gift-wrap flag if present ====== */
    if (properties._giftwrap) properties._giftwrap = 'true';

    try {
      const r = await fetch('/cart/change.js', {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({
          id        : item.key,
          quantity  : item.quantity,
          properties,
        }),
      });
      return r.json();
    } catch (err) {
      console.error('[RC Lead Time] Failed to persist lead time for', item.key, err);
      return null;
    }
  }

  /* ====== Main orchestrator ====== */

  /* ====== Entry point. **
   * 1. Fetches the current cart.
   * 2. Resolves the up-to-date lead time for every eligible line-item in parallel.
   * 3. Collects items whose lead time has actually changed (including blank → value).
   * 4. Updates the DOM immediately for those items (real-time UI update).
   * 5. Persists the new property values sequentially via /cart/change.js.
   * ====== */
  async function updateLeadTimes() {
    let cart;
    try {
      cart = await fetchCart();
    } catch (err) {
      console.error('[RC Lead Time] Could not fetch cart:', err);
      return;
    }

    if (!cart?.items?.length) return;

    /* ====== Track running totals per variant so the API gets the right unit count ====== */
    const variantCountMap = {};

    /* ====== Items that need a property update ====== */
    const itemsToUpdate = [];

    /* ====== STEP 1 : resolve lead times in parallel ====== */
    await Promise.all(
      cart.items.map(async (item) => {
        item.properties = item.properties || {};

        variantCountMap[item.variant_id] =
          (variantCountMap[item.variant_id] || 0) + item.quantity;

        if (shouldSkipItem(item)) return;

        /* ====== Bespoke products: never overwrite their custom lead time ====== */
        if (item.properties._is_bespoke_product === 'true') return;

        /* ====== The value already persisted in the line-item property ====== */
        const currentLeadTime = (item.properties[LEAD_TIME_KEY] || '').trim();

        let freshLeadTime = '';
        try {
          const res = await fetchLeadTime(item, variantCountMap[item.variant_id]);
          freshLeadTime = deriveLeadTime(res?.data ?? null, currentLeadTime);
        } catch (err) {
          console.error('[RC Lead Time] API error for variant', item.variant_id, err);
          return;
        }

        /* ====== Nothing useful came back from the API — leave item alone ====== */
        if (!freshLeadTime) return;

        /* ====== Value unchanged — no need to write back ====== */
        if (freshLeadTime.trim() === currentLeadTime) return;

        /* ====== Ensure _associated_unique_key exists — generate one if missing.
           This key ties the property list to a specific cart row and is required
           for accurate DOM targeting when the same variant appears more than once. ====== */
        if (!item.properties._associated_unique_key) {
          item.properties._associated_unique_key = (Math.random() + 1).toString(36).substring(2);
        }

        /* ====== Mark item for DOM update + persist ====== */
        item._newLeadTime = freshLeadTime;
        itemsToUpdate.push(item);
      })
    );

    if (!itemsToUpdate.length) return;

    /* ====== STEP 2 : patch the DOM immediately (real-time UI update) ====== */
    itemsToUpdate.forEach((item) => {
      updateLeadTimeInDOM(item, item._newLeadTime);
    });

    /* ====== STEP 3 : persist sequentially to avoid cart mutation races ====== */
    for (const item of itemsToUpdate) {
      await persistLeadTime(item);
    }
  }

  /* ====== Public API & auto-init ====== */

  /* ====== Expose a manual refresh on window so other scripts can trigger a re-run: window.RCLeadTime.refresh(); ====== */
  window.RCLeadTime = window.RCLeadTime || {};
  window.RCLeadTime.refresh = updateLeadTimes;

  /* ====== Auto-run on DOMContentLoaded (covers both drawer and full cart page).
   * The cache is per page-load, so repeated calls (e.g. after quantity change) are cheap when the variant/unit key hasn't changed. ====== */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateLeadTimes);
  } else {
    updateLeadTimes();
  }

  /* ====== Re-run whenever the cart drawer/page signals that items changed ====== */
  const CART_CHANGE_EVENTS = [
    'cart:updated',       // common custom event
    'cart:refresh',
    'rc:cart-updated',    // RC theme convention
  ];

  CART_CHANGE_EVENTS.forEach((evtName) => {
    document.addEventListener(evtName, () => {
      /* ====== Clear the cache so updated quantities get a fresh API call ====== */
      _leadTimeCache.clear();
      updateLeadTimes();
    });
  });

})();